import type { Uuid } from "../types/base.js";
import type { Item, PriceEntry, Product } from "../types/entities.js";

export interface PricePoint {
  observedAt: string;
  priceCents: number;
}

export interface PriceSeries {
  storeId: Uuid | null;
  points: PricePoint[];
  /** Jüngster Preis dieser Reihe. Die Reihe ist nie leer. */
  latestCents: number;
}

export interface BestStore {
  storeId: Uuid | null;
  priceCents: number;
  /** Um wie viel Prozent günstiger als der teuerste Laden, 1 bis 99. */
  savingsPercent: number;
  comparedToStoreId: Uuid | null;
  comparedToCents: number;
}

export interface OpenEstimate {
  totalCents: number;
  /** Artikel, für die noch nie ein Preis beobachtet wurde. */
  unknown: number;
  known: number;
}

const lebend = (entry: PriceEntry): boolean => entry.deleted_at === null;

/**
 * Preisverlauf eines Produkts, je Laden eine Reihe.
 *
 * Getrennt nach Laden und nicht als eine Linie, weil die interessante Frage
 * nicht "was hat es gekostet" ist, sondern "wo kostet es weniger". Eine
 * gemeinsame Linie über alle Läden zeigt nur das Zickzack des Wechselns.
 */
export function priceSeries(entries: readonly PriceEntry[], productId: Uuid): PriceSeries[] {
  const nachLaden = new Map<string, PricePoint[]>();

  for (const entry of entries) {
    if (!lebend(entry) || entry.product_id !== productId) continue;
    const schluessel = entry.store_id ?? "";
    const punkte = nachLaden.get(schluessel) ?? [];
    punkte.push({ observedAt: entry.observed_at, priceCents: entry.price_cents });
    nachLaden.set(schluessel, punkte);
  }

  return [...nachLaden.entries()]
    .map(([schluessel, punkte]) => {
      const sortiert = [...punkte].sort((a, b) => a.observedAt.localeCompare(b.observedAt));
      return {
        storeId: schluessel === "" ? null : schluessel,
        points: sortiert,
        latestCents: sortiert[sortiert.length - 1]?.priceCents ?? 0,
      };
    })
    .sort((a, b) => a.latestCents - b.latestCents);
}

/**
 * Wo das Produkt zuletzt am günstigsten war, und um wie viel.
 *
 * Verglichen werden die jeweils jüngsten Preise je Laden, nicht alle Preise:
 * dass etwas vor einem Jahr irgendwo billiger war, hilft beim nächsten
 * Einkauf nicht. Unter zwei Läden gibt es nichts zu vergleichen, dann null.
 */
export function bestStore(entries: readonly PriceEntry[], productId: Uuid): BestStore | null {
  const reihen = priceSeries(entries, productId);
  if (reihen.length < 2) return null;

  const guenstigste = reihen[0];
  const teuerste = reihen[reihen.length - 1];
  if (!guenstigste || !teuerste || teuerste.latestCents <= 0) return null;

  // Erst schwellen, dann runden. Andersherum würde aus einem halben Prozent
  // beim Runden ein ganzes, und ein Cent Unterschied stünde als Ersparnis da.
  const anteil = (teuerste.latestCents - guenstigste.latestCents) / teuerste.latestCents;
  if (anteil < 0.01) return null;
  const ersparnis = Math.round(anteil * 100);

  return {
    storeId: guenstigste.storeId,
    priceCents: guenstigste.latestCents,
    savingsPercent: ersparnis,
    comparedToStoreId: teuerste.storeId,
    comparedToCents: teuerste.latestCents,
  };
}

/**
 * Was die offenen Artikel einer Liste vermutlich kosten.
 *
 * Grundlage ist der zuletzt gezahlte Preis je Produkt, multipliziert mit der
 * Menge, aber nur bei Stückzahlen. Bei "500 g Hackfleisch" ist der letzte
 * Preis schon der Preis für die Packung, mal 500 wäre grober Unfug.
 *
 * Artikel ohne bekannten Preis werden gezählt statt geschätzt. Eine Zahl, die
 * so tut, als wüsste sie alles, ist schlimmer als eine ehrliche Lücke.
 */
export function estimateOpen(items: readonly Item[], products: readonly Product[]): OpenEstimate {
  const preise = new Map<string, number>();
  for (const product of products) {
    if (product.deleted_at === null && product.last_price_cents !== null) {
      preise.set(product.id, product.last_price_cents);
    }
  }

  let totalCents = 0;
  let unknown = 0;
  let known = 0;

  for (const item of items) {
    if (item.deleted_at !== null || item.is_checked) continue;

    const preis = item.product_id === null ? undefined : preise.get(item.product_id);
    if (preis === undefined) {
      unknown += 1;
      continue;
    }

    const faktor = item.unit === "stk" && item.quantity !== null && item.quantity > 0 ? item.quantity : 1;
    totalCents += Math.round(preis * faktor);
    known += 1;
  }

  return { totalCents, unknown, known };
}
